import type { QueryClient } from "@tanstack/react-query";
import type { GroomingBatchItemStatus, GroomingRunnerEvent } from "@ttc-spectree/toolbox-contract";
import { groomingKeys } from "@/hooks/queries/use-grooming";
import type { GroomingBatch } from "@/lib/api/grooming";

/**
 * The run workspace's batch state, from one source (epic PI-SPEC-SW-E280,
 * feature PI-SPEC-SW-1403).
 *
 * E279 merged runner events into page-local state on top of the batch read.
 * In the app a stopped-then-resumed run once finished showing "Post all (0)"
 * with two held briefs until a manual refresh. The root cause is an ordering
 * race: a batch read that started before the last event resolved after it and
 * overwrote newer state, and the page then had two sources that could
 * disagree.
 *
 * Now the React Query cache entry for the batch is the only state. Each runner
 * event is folded into that entry with {@link applyRunnerEvent}, after any
 * read still in flight is cancelled, and then the batch is read again. The
 * engine sends every event only after SpecTree has stored its effect
 * (`item-finished` only after SpecTree confirms the saved brief, `batch-*`
 * after the batch is finished), so a read started after an event can only be
 * as new as the event or newer, and a read started before it never lands.
 * On `batch-finished` and `batch-stopped` the caller awaits that read before
 * it clears its running flag, so the page never settles on a pre-event copy.
 */

/** The batch query's cache entry: the API response, `{ data: GroomingBatch }`. */
export interface BatchCacheEntry {
  data: GroomingBatch;
}

/** Item statuses that mean the run is done with the item. */
const FINISHED: ReadonlySet<GroomingBatchItemStatus> = new Set(["briefed", "not-ready", "failed", "skipped", "current"]);

/**
 * Batch event types that change what SpecTree stores, so a read follows them.
 * `queue-changed` (contract 3) is about the app's run queue, not one batch.
 */
type BatchEvent = Exclude<
  GroomingRunnerEvent,
  { type: "repo-status" } | { type: "item-progress" } | { type: "queue-changed" }
>;

/** True for an event about this batch that changes its state. */
export function isBatchStateEvent(event: GroomingRunnerEvent, batchId: string): event is BatchEvent {
  return (
    event.type !== "repo-status" &&
    event.type !== "item-progress" &&
    event.type !== "queue-changed" &&
    event.batchId === batchId
  );
}

/**
 * Fold one runner event into a batch. Pure. The item's brief summary is not
 * in the event, so an `item-finished` briefed item shows its size and counts
 * for Post all once the read that follows lands.
 */
export function applyRunnerEvent(batch: GroomingBatch, event: GroomingRunnerEvent): GroomingBatch {
  if (!isBatchStateEvent(event, batch.id)) return batch;
  switch (event.type) {
    case "item-started":
      return {
        ...batch,
        status: "running",
        stopReason: null,
        items: batch.items.map((item) =>
          item.id === event.batchItemId && !FINISHED.has(item.status) ? { ...item, status: "running" } : item
        ),
      };
    case "item-finished":
      return {
        ...batch,
        items: batch.items.map((item) =>
          item.id === event.batchItemId
            ? {
                ...item,
                status: event.outcome,
                errorCode: event.outcome === "briefed" ? null : (event.errorCode ?? item.errorCode),
                errorMessage: event.outcome === "briefed" ? null : (event.message ?? item.errorMessage),
              }
            : item
        ),
      };
    case "batch-stopped":
      // A stop puts the running item back in the queue without an item-finished event.
      return {
        ...batch,
        status: "stopped",
        stopReason: event.reason,
        items: batch.items.map((item) => (item.status === "running" ? { ...item, status: "queued" } : item)),
      };
    case "batch-finished":
      return { ...batch, status: event.status };
  }
}

/** {@link applyRunnerEvent} over the cache entry; an empty entry stays empty. */
export function applyRunnerEventToEntry(
  entry: BatchCacheEntry | undefined,
  event: GroomingRunnerEvent
): BatchCacheEntry | undefined {
  if (!entry) return entry;
  const next = applyRunnerEvent(entry.data, event);
  return next === entry.data ? entry : { ...entry, data: next };
}

/**
 * Apply one runner event to the batch's cache entry, then read the batch
 * again. Resolves once that read has landed (or failed), so a caller can
 * await it before it treats the run as settled.
 *
 * Order matters and is synchronous up to the read: cancelling an in-flight
 * read reverts the entry to its state before that read started, so the event
 * is applied after the cancel, never before.
 */
export async function applyRunnerEventToCache(
  queryClient: QueryClient,
  batchId: string,
  event: GroomingRunnerEvent
): Promise<void> {
  if (!isBatchStateEvent(event, batchId)) return;
  const queryKey = groomingKeys.batch(batchId);
  const cancelled = queryClient.cancelQueries({ queryKey, exact: true });
  queryClient.setQueryData<BatchCacheEntry>(queryKey, (entry) => applyRunnerEventToEntry(entry, event));
  await cancelled;
  try {
    await queryClient.invalidateQueries({ queryKey, exact: true });
  } catch {
    // A failed read leaves the event's state in place; the next event or poll reads again.
  }
}
